import {orderBy, upperFirst} from "lodash-es";
import cx from "classnames";
import {friendlyValidity, getLocaleFromUrl, i18n} from "../../i18n/I18n";
import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {inject} from "../../services/di";
import {LocationInfo, LocationService} from "../../services/LocationService";
import {FlyerService} from "../../services/FlyerService";
import {SeoService} from "../../services/SeoService";
import {Flyer} from "../../definitions/FlyerDefinitions";
import {Page} from "../flipp-page/Page";
import {UserAgentService} from "../../services/UserAgentService";
import {slugify} from "../../utils/slugify";
import {DateTimeService} from "../../services/DateTimeService";
import {LinkService} from "../../services/LinkService";
import {FlyerListingItem, FlyerListingItemContext} from "../flipp-flyer-listing-item/FlyerListingItem";
import {SeoDataService} from "../../services/SeoDataService";
import {FlyerMetadataJSON, MerchantDetails} from "../../definitions/BackflippDefinitions";
import MerchantService from "../../services/MerchantService";
import {Link} from "../flipp-link/Link";

type CityStorePageContentModel = [
	Flyer,
	StoreFlyers,
	StoreFlyers,
	Flyer[],
	string[],
	MerchantDetails
];

interface LinkData {
	id?: string;
	name: string;
	link?: string;
}

interface CityStorePageAttributes {
	location: string;
	store: string;
}

const IMAGES_PATH = "https://f.wishabi.net/creative/Distribution/images";

@element("flipp-city-store-page")
export class CityStorePage extends Component<CityStorePageAttributes, any> {
	@attr location: string = "";
	@attr store: string = "";

	@inject locationService: LocationService;
	@inject userAgent: UserAgentService;
	@inject flyerService: FlyerService;
	@inject linkService: LinkService;
	@inject seoService: SeoService;
	@inject seoDataService: SeoDataService;
	@inject merchantService: MerchantService;

	seoContent: string;
	shouldHideSeoThumbnail: boolean;
	featuredFlyerLink: string;
	locationByPostalCode: LocationInfo;

	async prepare() {
		const locationInfo = await this.locationService.getLocationInfo();
		const merchantId = this.getMerchantIdFromUrl();
		const requestsArr = [
			this.flyerService.getFlyersDataJSON(locationInfo.postal_code),
			this.seoService.getSeoContent(this.props.store, this.locationService.locale, this.props.location),
			this.seoDataService.getTopCanadaMerchants()
		];
		if (merchantId) {
			requestsArr.push(this.merchantService.getMerchantDetails(merchantId));
		}
		const [ flyersData, seoContent, topMerchants, merchantData] = await Promise.all(requestsArr);

		let merchantDetails;
		if (merchantData) {
			merchantDetails = merchantData.merchants[0];
		}

		this.seoContent = seoContent;

		const flyers = (flyersData.flyers as FlyerMetadataJSON[]).map(flyer => new Flyer(flyer));

		if (merchantId) {
			const merchantFlyer = flyers.find(f => f.merchantId.toString() === merchantId);
			if (merchantFlyer && slugify(merchantFlyer.merchant) !== this.props.store) {
				const correctMerchantName = slugify(merchantFlyer.merchant);
				const locale = this.locationService.locale;
				const location = this.props.location;
				const storesPartUrl = `stores/${correctMerchantName}${window.location.search}`;
				const url = this.props.location ? `/${locale}/${location}/${storesPartUrl}` : `/${locale}/${storesPartUrl}`;
				window.history.pushState({}, "", url);
			}
		}

		const { featuredFlyer, currentFlyers, upcomingFlyers, popularFlyers } = splitStoreFlyers(
			flyers,
			this.props.store,
			merchantId,
			new Date(),
		);
		if (!this.props.location) {
			this.locationByPostalCode = await this.locationService.getLocationInfo();
		}
		if (featuredFlyer) {
			const prefix = this.props.location ?
				`${this.locationService.locale}/${this.props.location}` :
				this.getLocationPrefix();
			this.featuredFlyerLink =  this.linkService.getFlyerLink(featuredFlyer, prefix);
			if (!merchantDetails) {
				const merchantData = await this.merchantService.getMerchantDetails(featuredFlyer.merchantId.toString());
				merchantDetails = merchantData.merchants[0];
			}
		}

		return [featuredFlyer, currentFlyers, upcomingFlyers, popularFlyers, topMerchants, merchantDetails] as CityStorePageContentModel;
	}

	getMerchantIdFromUrl() {
		const urlSearchParams = new URLSearchParams(window.location.search);
		return urlSearchParams.get("merchant_id");
	}

	onRender() {
		super.onRender();
		setTimeout(() => {
			const el = document.getElementById("seoBody");
			if (el && this.seoContent) {
				el.innerHTML = this.seoContent;
				const seoEl = document.getElementById("seoContent");
				seoEl.style.display = "block";
			}
		});
		if (this.shouldHideSeoThumbnail) {
			this.hideSeoThumbnail();
		}
	}

	getLocationPrefix() {
		return `/${getLocaleFromUrl()}/${slugify(this.locationByPostalCode.city_name)}-${slugify(this.locationByPostalCode.region_name)}`;
	}

	getDisplayStoreName(merchantDetails: any) {
		return merchantDetails ? merchantDetails.name : this.props.store.split("-").map((item) => upperFirst(item)).join(" ");
	}

	async handleFlyerOpenClick () {
		window.location.href = this.featuredFlyerLink;
	}

	hideSeoThumbnail() {
		setTimeout(() => {
			const els = document.getElementsByClassName("seo-thumbnail");
			Array.from(els).forEach(function (element) {
				(element as HTMLElement).style.display = "none";
			});
		});
	}

	scrollToSection(id: string) {
		const element = document.getElementById(id);
		element.scrollIntoView({ behavior: "smooth" });
	}

	onSeoThumbnailImageError() {
		this.hideSeoThumbnail();
		this.shouldHideSeoThumbnail = true;
	}

	renderSeoThumbnail(storeName: string, city: string) {
		return (
			<a href={this.featuredFlyerLink} class="seo-thumbnail">
				<img
					src={`${IMAGES_PATH}/assets/store-seo-thumbnails/${slugify(storeName)}.webp`}
					onError={this.onSeoThumbnailImageError.bind(this)}
					alt={i18n("store_seoThumbnail", {
						storeName,
						city,
					})}
					title={
						this.props.location
							? i18n("store_location_title", {
								storeName,
								city,
							})
							: i18n("store_title", {
								storeName,
							})
					}
				/>
			</a>
		);
	}

	renderStoreFlyerInfo(featuredFlyer: Flyer, currentFlyersCount: number, upcomingFlyersCount: number, merchantDetails: MerchantDetails) {
		const city = this.props.location ? getCityNameFromLocation(this.props.location) : "";
		const provinceAbbreviated = this.props.location ? extractStateFromLocation(this.props.location) : "";

		let storeName = this.getDisplayStoreName(merchantDetails);

		return (
			<div class="info-container">
				<div class="store-flyer-info">
					<div class="store-info">
						{merchantDetails ? <img class="logo" src={merchantDetails.storefront_logo_url} alt={storeName} /> : null}
						<div class="text">
							<div class="store-name">{this.props.location ? i18n("store_location_title", {
								storeName,
								city,
							}) : i18n("store_no_location_title", {storeName})}
							</div>
						</div>
					</div>
					<div class="flyers-count">
						<div id="currentFlyersCount" class={cx("block", {"clickable": currentFlyersCount > 1 } )} onClick={() => {
							this.scrollToSection("currentFlyers");
						}}>
							<div class={cx("number", {"clickable": currentFlyersCount > 1 } )}>{currentFlyersCount}</div>
							<div class={cx("text", {"clickable": currentFlyersCount > 1 } )}>{currentFlyersCount > 1 ? i18n("flyers") : i18n("flyer")}</div>
						</div>
						<div class="divider" />
						<div id="upcomingFlyersCount" class={cx("block", {"clickable": upcomingFlyersCount > 0 } )} onClick={() => {
							this.scrollToSection("upcomingFlyers");
						}}>
							<div class={cx("number", {"clickable": upcomingFlyersCount > 0 } )}>{upcomingFlyersCount}</div>
							<div class={cx("text", {"clickable": upcomingFlyersCount > 0 } )}>{i18n("upcoming")}</div>
						</div>
						{featuredFlyer ? <div class="seo-thumbnail-desktop">{this.renderSeoThumbnail(storeName, city)}</div> : null}
					</div>
				</div>
				{featuredFlyer ?
					<div>
						<div class="flyer-info-mobile">
							<div class="flyer-start-date">
								<div class="store-name">{storeName} Flyer</div>
								{friendlyValidity(featuredFlyer.validFrom, featuredFlyer.validTo)}
							</div>
							{this.renderSeoThumbnail(storeName, city)}
						</div>
						<div class="flyer-info-desktop">
							<div class="flyer-start-date">
								{this.props.location ? <span class="geolocation">{`${city}, ${provinceAbbreviated}`}&nbsp;-&nbsp;</span> : null}
								{friendlyValidity(featuredFlyer.validFrom, featuredFlyer.validTo)}
							</div>
							<div class="horizontal-divider"/>
						</div>
					</div>
					: null }

				{featuredFlyer ? this.renderFeaturedFlyer(featuredFlyer) : this.renderNoFlyers(storeName, city)}
			</div>
		);
	}

	renderFeaturedFlyer(featuredFlyer: Flyer) {
		return (
			<div class="featured-flyer-container">
				<FlyerListingItem style={{width: "auto"}} flyerId={featuredFlyer.id} premium context={FlyerListingItemContext.CityStore} category={"All Flyers"}/>
				<div class="white-gradient"></div>
				<Link to={featuredFlyer}>
					<button
						class={cx("open-button", {"fr": this.locationService.locale === "fr-ca"})}>
						{i18n("openFlyer")}
					</button>
				</Link>
			</div>
		);
	}

	renderNoFlyers(storeName: string, city: string) {
		return (
			<div class="no-flyers-container">
				<div class="no-flyers-inner">
					{city && <div>{i18n("store_noFlyers", { storeName, city })}</div>}
					{!city && <div>{i18n("store_noFlyers_noCity", { storeName })}</div>}
					<img src={`${IMAGES_PATH}/assets/no_flyers.png`} />
				</div>
			</div>
		);
	}

	renderDownloadAppBanner() {
		const isFrench = this.locationService.locale === "fr-ca";
		const appStoreImage = isFrench ? "Badge-Appstore-fr.png" : "Badge-Appstore-300x89.png";
		const googlePlayImage = isFrench ? "Badge-Googleplay-fr.png" : "Badge-Google-300x90.png";
		return (
			<div class="download-banner">
				<img width={180} height={160} src={`${IMAGES_PATH}/assets/banner/phone.png`} />
				<div class="promoText">{i18n("downloadAppBanner_promo")}</div>
				<div class="qr">
					<p>{i18n("downloadAppBanner_qr")}</p>
					<img width={80} height={80} src={`${IMAGES_PATH}/assets/banner/qrcode.png`} />
				</div>
				<div class="buttons">
					<a href="https://click.flipp.com/h8pE/8754848d" target="_blank" rel="noreferrer">
						<img width={140} height={40} src={`${IMAGES_PATH}/assets/banner/${appStoreImage}`} />
					</a>
					<a href="https://click.flipp.com/h8pE/8754848d" target="_blank" rel="noreferrer">
						<img width={140} height={40} src={`${IMAGES_PATH}/assets/banner/${googlePlayImage}`} />
					</a>
				</div>
				<img width={100} height={120} src={`${IMAGES_PATH}/assets/banner/strawberry.png`} />
			</div>
		);
	}

	renderCityStoreLink(storeName: string, cityName: string, location: string  ) {
		const name = `${storeName} ${cityName}`;
		return (
			<div class="link-container">
				<Link  to={"store"} params={{location, name: slugify(storeName)}}>
					<span class="link-button">{name}</span>
				</Link>
			</div>
		);
	}

	renderBreadcrumbs(merchantDetails: any) {
		const storeName = this.getDisplayStoreName(merchantDetails);
		const locale = this.locationService.locale;
		const location = this.props.location;
		const bc = [
			{ name: i18n("home"), link: "/" },
			{ name: getCountryNameByLocale(locale), link: `/${locale}` },
		] as LinkData[];
		if (location) {
			bc.push({ id: "location", name: getCityNameFromLocation(location), link: `/${locale}/${location}` });
			bc.push({ id: "store", name: i18n("stores"), link: `/${locale}/${location}/stores` });
		} else {
			bc.push({ id: "store", name: i18n("stores"), link: `/${locale}/stores` });
		}
		bc.push({ name: storeName });
		return (
			<h3>
				{
					bc.map((link, index) => {
						if (link.id) {
							return (<Link
								to={link.id}
								params={{location}}
							>
								<span>{link.name}/</span>
							</Link>);
						}
						return (
							<span>
							{link.link ? <a href={link.link}>{link.name}</a> : <span>{link.name}</span>}
								{index === bc.length - 1 ? "" : "/"}
							</span>
						);
					}) }
			</h3>
		);
	}

	render([featuredFlyer, currentFlyers, upcomingFlyers, popularFlyers, topMerchants, merchantDetails]: CityStorePageContentModel) {
		const displayStoreName = this.getDisplayStoreName(merchantDetails);
		const displayCityName = this.props.location ? getCityNameFromLocation(this.props.location) : "";

		return (
			<Page ref="page" show="flyers">
				<div class="store-page">
					<div class="breadcrumbs">
						{this.renderBreadcrumbs(merchantDetails)}
					</div>
					<div class="store-page-content">
						<div class="left-pane">
							{this.renderStoreFlyerInfo(featuredFlyer, currentFlyers.count, upcomingFlyers.count, merchantDetails)}
							{this.renderDownloadAppBanner()}
						</div>
						<div class="right-pane">
							<div class="flyers">
								{currentFlyers.items.length > 0 && (
									<div id="currentFlyers">
										<div class="flyers-type-title">
											{i18n("store_currentFlyers", { storeName: displayStoreName })}
										</div>
										<div class="flyers-container">
											{currentFlyers.items.map((f) => {
												return (<FlyerListingItem flyerId={f.id} context={FlyerListingItemContext.CityStore} category={"All Flyers"}/>);
											})}
										</div>
									</div>
								)}
								{upcomingFlyers.items.length > 0 && (
									<div id="upcomingFlyers">
										<div class="flyers-type-title">
											{i18n("store_upcomingFlyers", { storeName: displayStoreName })}
										</div>
										<div class="flyers-container">
											{upcomingFlyers.items.map((f) => {
												return (<FlyerListingItem flyerId={f.id} context={FlyerListingItemContext.CityStore} category={"All Flyers"}/>);
											})}
										</div>
									</div>
								)}
								<div id="popularFlyers" >
									<div class="flyers-type-title">
										{displayCityName ? i18n("store_popularCityFlyers", { city: displayCityName }) :  i18n("store_popularFlyers")}
									</div>
									<div class="flyers-container">
										{popularFlyers.map((f) => {
											return (<FlyerListingItem flyerId={f.id} context={FlyerListingItemContext.CityStore} category={"All Flyers"}/>);
										})}
									</div>
								</div>

							</div>
							<div class="links-container">
								<div class="flyers-type-title">{i18n("store_storeCityFlyers", { store: displayStoreName })}</div>
								{TOP_CITIES.map((city) => {
									const location = `${slugify(city.name)}-${city.state.toLowerCase()}`;
									return (
										this.renderCityStoreLink(displayStoreName, city.name, location)
									);
								})}
							</div>
							<div id="seoContent" class="seo-content" >
								<div id="seoBody" class="seo-body" />
							</div>
							<div class="about">
								<h3>{i18n("store_aboutFlipp_title")}</h3>
								<p>{i18n("store_aboutFlipp_text")}</p>
							</div>
							<div class="footer-item">
								<h3>{i18n("footer_retailers")}</h3>
								<div class="footer-container">
									{topMerchants.slice(0, 10).map((store) => {
										return (
											<Link to={"store"} params={{location: this.props.location, name: slugify(store)}}>
												<span>{store}</span>
											</Link>
										);
									})}
								</div>
							</div>
							<div class="footer-item">
								<h3>{i18n("footer_locations")}</h3>
								<div class="footer-container">
									{TOP_CITIES.map((city) => {
										return (
											<Link to={"city_flyers"} params={{location: `${slugify(city.name)}-${city.state.toLowerCase()}`}}>
												<span>{city.name}</span>
											</Link>
										);
									})}
								</div>
							</div>
							<div class="footer-item">
								<h3>{i18n("footer_company")}</h3>
								<div class="footer-container">
									<div>
										<a href="https://blog.flipp.com/">{i18n("footer_blog")}</a>
									</div>
									<div>
										<a href="https://corp.flipp.com/partners/">
											<a>{i18n("footer_retailers")}</a>
										</a>
									</div>
									<div>
										<a href="https://corp.flipp.com/job-openings/">
											<a>{i18n("footer_careers")}</a>
										</a>
									</div>
									<div>
										<Link to={"store"} params={{location: null, name: null}}>
											<span>{i18n("footerStores")}</span>
										</Link>
									</div>
									<div>
										<a href={"https://help.flipp.com/"}>{i18n("helpCenter")}</a>
									</div>
								</div>
							</div>
							<div class="copyright">
								{i18n("footer_copyright")}
							</div>
						</div>
					</div>
				</div>
			</Page>
		);
	}
}

function extractStoreFlyers(flyers: Flyer[], storeName: string, merchantId: string): Flyer[] {
	return flyers.filter((f) => {
		if (merchantId) {
			return f.merchantId.toString() === merchantId;
		}
		const merchant = slugify(f.merchant);
		return merchant.includes(storeName);
	});
}

interface StoreFlyers {
	items: Flyer[];
	count: number;
}

const POPULAR_FLYERS_COUNT = 8;

function splitStoreFlyers(
	flyerList: Flyer[],
	storeName: string,
	merchantId: string,
	nowDate: Date,
): {
	featuredFlyer: Flyer;
	currentFlyers: StoreFlyers;
	upcomingFlyers: StoreFlyers;
	popularFlyers: Flyer[];
} {
	const dtService = DateTimeService.getInstance();

	const flyers = extractStoreFlyers(flyerList, storeName, merchantId);
	const premiumFlyers = flyers.filter((f) => f.premium === true);
	const normalFlyers = flyers.filter((f) => f.premium === false);
	const sortedFlyers = [...orderBy(premiumFlyers, ["validFrom"]), ...orderBy(normalFlyers, ["validFrom"])];
	const featuredFlyer = sortedFlyers[0];
	const now = dtService.fromJsDate(nowDate);
	const currentFlyers = { items: [], count: 0 } as any;
	const upcomingFlyers = { items: [], count: 0 } as any;
	let arr, from;
	sortedFlyers.map((f) => {
		from = dtService.fromJsDate(f.validFrom);
		if (from > now) {
			arr = upcomingFlyers.items;
			upcomingFlyers.count += 1;
		} else {
			arr = currentFlyers.items;
			currentFlyers.count += 1;
		}
		if (f.id !== featuredFlyer.id) {
			arr.push(f);
		}
	});
	const popularFlyers = orderBy(flyerList, ["validFrom"])
		.filter((f) => slugify(f.merchant) !== storeName && f.premium === true)
		.slice(0, POPULAR_FLYERS_COUNT);

	return { featuredFlyer, currentFlyers, upcomingFlyers, popularFlyers };
}

const getCityNameFromLocation = (location: string) => {
	const city = extractCityFromLocation(location);
	return city
		.split(" ")
		.map((c: string) => upperFirst(c))
		.join(" ");
};

function extractCityFromLocation(location: string): string {
	return location.split("-").slice(0, -1).join(" ");
}

function extractStateFromLocation(location: string): string {
	return upperFirst(location.split("-").pop());
}

export enum Country {
	US = "USA",
	CA = "Canada",
}

function getCountryNameByLocale(locale: string): Country {
	switch (locale) {
		case "en-us":
			return Country.US;
		case "en-ca":
			return Country.CA;
		case "fr-ca":
			return Country.CA;
		default:
			return Country.CA;
	}
}

const TOP_CITIES = [
	{
		"name": "Toronto",
		"state": "ON"
	},
	{
		"name": "Montreal",
		"state": "QC"
	},
	{
		"name": "Calgary",
		"state": "AB"
	},
	{
		"name": "Ottawa",
		"state": "ON"
	},
	{
		"name": "Edmonton",
		"state": "AB"
	},
	{
		"name": "Mississauga",
		"state": "ON"
	},
	{
		"name": "Winnipeg",
		"state": "MB"
	},
	{
		"name": "Vancouver",
		"state": "BC"
	},
	{
		"name": "Brampton",
		"state": "ON"
	},
	{
		"name": "Hamilton",
		"state": "ON"
	}
];
